import React, { useState } from "react";
import Papa from "papaparse";
import { db } from "@/config/firebase";
import { addDoc, collection, doc, getDocs, query, serverTimestamp, where, setDoc } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { initializeApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, updateProfile, fetchSignInMethodsForEmail, signOut } from "firebase/auth";

const CSV_URL = process.env.REACT_APP_DEFAULT_ROSTER_CSV_URL || (import.meta && import.meta.env && import.meta.env.REACT_APP_DEFAULT_ROSTER_CSV_URL) || "";

const toDisplayName = (first) => String(first||"").trim().split(/\s+/)[0].replace(/^./, c=>c.toUpperCase());
const usernameFrom = (first, last) => {
  const f = toDisplayName(first);
  const initial = String(last||"").trim().charAt(0).toUpperCase();
  return `${f}${initial}`; // e.g., DanielL
};

export default function ImportRoster() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [authSummary, setAuthSummary] = useState({ created:0, skipped:0, errors:0 });

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, { header: true, skipEmptyLines: true, complete: (results) => { setRows(results.data || []); }, });
  };

  const loadFromUrl = () => {
    setStatus("Lade CSV von URL…");
    Papa.parse(CSV_URL, { header: true, download: true, skipEmptyLines: true, complete: (results) => { setRows(results.data || []); setStatus(`Geladen: ${results.data?.length||0} Zeilen`); }, error: () => setStatus("Fehler beim Laden der CSV.") });
  };

  const importAuthAndUsers = async () => {
    setStatus("Erstelle Firebase-Accounts…");
    const secondaryConfig = {
      apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
      authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
      projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
      storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
      appId: process.env.REACT_APP_FIREBASE_APP_ID,
    };
    const secondaryApp = initializeApp(secondaryConfig, "importer");
    const auth = getAuth(secondaryApp);

    let created=0, skipped=0, errors=0;

    for (const r of rows) {
      try {
        const first = (r.firstname || r.first_name || r.FirstName || r["first name"] || "").trim();
        const last = (r.lastname || r.last_name || r.LastName || r["last name"] || "").trim();
        const tna = String(r["tna-number"] || r.tna_number || r.TNA || r.number || "").trim();
        if (!first || !last || !tna) { skipped++; continue; }
        const username = usernameFrom(first, last);
        const email = `${username}@rhein-stars.app`;
        const q = query(collection(db, "users"), where("tna_number","==", tna));
        const existingByTna = await getDocs(q);
        const methods = await fetchSignInMethodsForEmail(auth, email);
        if (!existingByTna.empty || (methods && methods.length)) { skipped++; continue; }
        const cred = await createUserWithEmailAndPassword(auth, email, "26071979");
        try { await updateProfile(cred.user, { displayName: toDisplayName(first) }); } catch (_) {}
        const display_name = toDisplayName(first);
        await setDoc(doc(db, "users", cred.user.uid), { name: display_name, display_name, tna_number: tna, role: "player", created_at: serverTimestamp(), auth_uid: cred.user.uid, email, }, { merge: true });
        created++;
      } catch (e) { errors++; }
    }

    try { await signOut(auth); } catch (_) {}
    setAuthSummary({ created, skipped, errors });
    setStatus(`Fertig. Neu: ${created}, Übersprungen: ${skipped}, Fehler: ${errors}`);
  };

  return (
    <div className="pb-28 min-h-screen bg-[#0e0b0c] text-rose-50 px-4 pt-6">
      <h2 className="text-xl font-bold mb-4">Kader importieren</h2>
      <Card className="bg-[#1a0d11] border-white/10">
        <CardContent className="pt-4 space-y-4">
          <div className="flex gap-2 items-center">
            <input data-testid="import-file" type="file" accept=".csv" onChange={onFile} className="text-sm" />
            <Button data-testid="import-url" onClick={loadFromUrl} className="bg-white/10 hover:bg-white/20">Von URL laden</Button>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button data-testid="import-auth-users" onClick={importAuthAndUsers} className="bg-[#b40023] hover:bg-[#97001d]">Accounts + users anlegen</Button>
          </div>
          {status && <p data-testid="import-status" className="text-sm text-rose-200/80">{status}</p>}
          {(authSummary.created+authSummary.skipped+authSummary.errors>0) && (
            <p data-testid="import-summary" className="text-xs text-rose-200/70">Neu: {authSummary.created} • Übersprungen: {authSummary.skipped} • Fehler: {authSummary.errors}</p>
          )}
          <p className="text-xs text-rose-200/60">Benutzername: Vorname + Initiale Nachname (z.B. DanielL) • E-Mail: {"{username}"}@rhein-stars.app • Passwort: 26071979</p>
        </CardContent>
      </Card>
    </div>
  );
}
